/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledDateTimePicker,
  ControlledInput,
  ControlledMonthYearPicker,
  ControlledSelect,
  FormInput,
  FormMonthYearPicker,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  ITomraMachineRevenueHistory,
  useTomraMachines,
} from "../../store/useTomraMachines";
import { useDropLocations } from "../../store/useDropLocations";
import { FLOAT_REGEX } from "../../utils/regexValidations";
import dayjs from "dayjs";

type Inputs = {
  name: string;
  serialNumber: string;
  location: string;
  status?: "available" | "not_available";
  unavailableUntil?: Date | null;
  showUnavailableUntilTime?: boolean;
};

function EditTomraMachineScreen() {
  /** Params */
  const params = useParams();
  /** Hooks */
  const { dropLocations, getDropLocations } = useDropLocations();
  const {
    updateTomraMachine,
    updatedTomraMachine,
    clearUpdatedTomraMachine,
    getTomraMachine,
    tomraMachine,
    addRevenueHistory,
    updateRevenueHistory,
    deleteRevenueHistory,
  } = useTomraMachines();
  const navigate = useNavigate();
  const {
    reset,
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();
  const selectedStatus = watch("status");
  const {
    reset: resetHistory,
    control: historyControl,
    watch: watchHistory,
    handleSubmit: handleHistorySubmit,
    formState: { errors: historyErrors },
  } = useForm<ITomraMachineRevenueHistory>();
  /** State */
  const [droppieRevenueHistory, setDroppieRevenueHistory] = useState<
    ITomraMachineRevenueHistory[]
  >([]);

  /** Effects */
  useEffect(() => {
    if (updatedTomraMachine) {
      clearUpdatedTomraMachine();
      navigate(-1);
    }
  }, [updatedTomraMachine]);

  useEffect(() => {
    getTomraMachine(params?.id as string);
    getDropLocations();
  }, []);

  useEffect(() => {
    if (tomraMachine) {
      reset({
        name: tomraMachine?.name,
        serialNumber: tomraMachine?.serialNumber,
        location: tomraMachine?.location,
        status: tomraMachine?.status ?? "available",
        unavailableUntil: tomraMachine?.unavailableUntil
          ? new Date(tomraMachine.unavailableUntil)
          : null,
        showUnavailableUntilTime: tomraMachine?.showUnavailableUntilTime ?? false,
      });
      if (tomraMachine?.droppie_revenue_history) {
        setDroppieRevenueHistory(tomraMachine?.droppie_revenue_history);
      }
    }
  }, [tomraMachine]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    updateTomraMachine(params?.id as string, {
      ...data,
      unavailableUntil:
        data?.status === "not_available" && data?.unavailableUntil
          ? data.unavailableUntil.toISOString()
          : null,
    });
  };

  const onHistorySubmit = async (data: ITomraMachineRevenueHistory) => {
    addRevenueHistory(params?.id as string, data);
    resetHistory({
      startDate: new Date(),
      endDate: null,
      amount: 0,
    });
  };

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit Statiegeld Product
        </h1>
      </div>
      <div className="flex flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Update Statiegeld Product details
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Statiegeld Product name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.serialNumber)}
              label="Serial number"
              placeholder="Tomra machine serial number"
              name="serialNumber"
              rules={{ required: true }}
            />
            <ControlledSelect
              name="location"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.location)}
              label="Location"
              // @ts-ignore
              options={dropLocations?.map((location) => ({
                // @ts-ignore
                title: location?.cost_place as string,
                // @ts-ignore
                _id: location._id as string,
              }))}
              placeholder="Pick location here"
            />
            <ControlledSelect
              name="status"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.status)}
              label="Machine status"
              options={[
                { _id: "available", title: "Available" },
                { _id: "not_available", title: "Not available" },
              ]}
              placeholder="Pick status"
            />
            {selectedStatus === "not_available" ? (
              <div className="mb-4">
                <ControlledDateTimePicker
                  name="unavailableUntil"
                  control={control}
                  rules={{ required: false }}
                  error={Boolean(errors?.unavailableUntil)}
                  label="Unavailable until"
                  placeholder="Pick date and time"
                />
                <SecondaryButton
                  type="button"
                  onClick={() => setValue("unavailableUntil", null)}
                  label="Clear unavailable date"
                />
                <Controller
                  control={control}
                  name="showUnavailableUntilTime"
                  render={({ field: { value, onChange } }) => (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer font-satoshi text-sm text-dark select-none">
                      <input
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        className="w-4 h-4 accent-[#FF6E41] cursor-pointer"
                      />
                      Show time in app
                    </label>
                  )}
                />
              </div>
            ) : null}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
        <div className="w-full sm:w-1/3 bg-white border border-gray-border drop-shadow-md sm:ml-2">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Statiegeld Machine Revenue history
            </p>
          </div>
          <form
            onSubmit={handleHistorySubmit(onHistorySubmit)}
            className="w-full px-7 py-7"
          >
            {droppieRevenueHistory?.length ? (
              droppieRevenueHistory.map((history, index) => (
                <div key={index}>
                  <div className="mb-2 font-satoshi font-medium text-base text-dark">
                    #{index + 1} - {dayjs(history?.startDate).format("MM/YYYY")}{" "}
                    -{" "}
                    {history?.endDate
                      ? dayjs(history?.endDate).format("MM/YYYY")
                      : "Still active"}
                  </div>
                  <FormMonthYearPicker
                    error={Boolean(historyErrors?.startDate)}
                    label="Start date"
                    placeholder="Add Start Date here..."
                    value={history?.startDate}
                    onChange={(date) => {
                      setDroppieRevenueHistory((prev) => {
                        prev[index].startDate = date;
                        return [...prev];
                      });
                    }}
                  />
                  <FormMonthYearPicker
                    error={Boolean(historyErrors?.startDate)}
                    label="End date"
                    placeholder="Still active..."
                    value={history?.endDate ?? null}
                    onChange={(date) => {
                      setDroppieRevenueHistory((prev) => {
                        prev[index].endDate = date;
                        return [...prev];
                      });
                    }}
                  />
                  <FormInput
                    label="Revenue (month)"
                    placeholder="Add amount per month"
                    value={String(history?.amount)}
                    onChange={(e) => {
                      setDroppieRevenueHistory((prev) => {
                        prev[index].amount = Number(e.target.value);
                        return [...prev];
                      });
                    }}
                  />
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        updateRevenueHistory(
                          params?.id as string,
                          index,
                          history
                        );
                      }}
                      className={`w-[100px] h-[46px] px-4 rounded font-satoshi text-sm font-medium bg-blue-300 text-white hover:bg-blue-500 mt-4`}
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        deleteRevenueHistory(params?.id as string, index);
                      }}
                      className={`w-[100px] h-[46px] px-4 rounded font-satoshi text-sm font-medium bg-red-300 text-white hover:bg-red-500 mt-4 ml-2`}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="w-full h-[1px] bg-gray-border my-4" />
                </div>
              ))
            ) : (
              <div className="flex flex-row items-center justify-center">
                <p className="font-satoshi font-medium text-base text-dark">
                  No revenue history found
                </p>
                <div className="w-full h-[1px] bg-gray-border my-4" />
              </div>
            )}
            <div className="my-2 font-satoshi font-medium text-base text-dark">
              Add new month revenue
            </div>
            <ControlledMonthYearPicker
              name="startDate"
              rules={{ required: true }}
              control={historyControl}
              error={Boolean(historyErrors?.startDate)}
              label="Start date"
              placeholder="Add Start Date here..."
            />
            <ControlledMonthYearPicker
              name="endDate"
              rules={{ required: false }}
              control={historyControl}
              error={Boolean(historyErrors?.endDate)}
              label="End date"
              placeholder="Add End Date here..."
            />
            <ControlledInput
              name="amount"
              control={historyControl}
              label="Revenue (month)"
              placeholder="Add amount per month"
              type="text"
              rules={{ required: false, pattern: FLOAT_REGEX }}
              error={Boolean(
                historyErrors?.amount ||
                // @ts-ignore
                !FLOAT_REGEX.test(watchHistory("amount") ?? 0)
              )}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Add" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditTomraMachineScreen);
